import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface UserProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
}

export interface FollowedTransmission {
  id: number;
  title: string;
  author: string;
  cover_url: string | null;
  created_at: string;
  user_id: string;
  user_display_name?: string;
}

/**
 * One fetch per user per list, shared by every useFollowing() instance.
 *
 * /community mounts this hook twice (NetworkSection, WhoToFollow), and its
 * effect depended on the `user` OBJECT, which auth replaces on every event
 * (initial session, sign-in, token refresh) even when the person is the same.
 * Measured live: 12x user_follows + 12x get_public_profiles per page load.
 * Keyed on the user id now, and instances share the in-flight request for a
 * short window; follow/unfollow drop the cache.
 */
const FOLLOW_TTL_MS = 30_000;
const followCache = new Map<string, { at: number; promise: Promise<UserProfile[]> }>();

function invalidateFollowCache() {
  followCache.clear();
}

async function loadFollowList(userId: string, kind: 'following' | 'followers'): Promise<UserProfile[]> {
  const key = `${kind}:${userId}`;
  const hit = followCache.get(key);
  if (hit && Date.now() - hit.at < FOLLOW_TTL_MS) return hit.promise;

  const promise = (async () => {
    let ids: string[];
    if (kind === 'following') {
      const { data, error } = await supabase.from('user_follows').select('following_id').eq('follower_id', userId);
      if (error) throw error;
      ids = (data ?? []).map(r => r.following_id);
    } else {
      const { data, error } = await supabase.from('user_follows').select('follower_id').eq('following_id', userId);
      if (error) throw error;
      ids = (data ?? []).map(r => r.follower_id);
    }
    if (ids.length === 0) return [];
    const { data: profiles, error: profileError } = await supabase.rpc('get_public_profiles', { _ids: ids });
    if (profileError) throw profileError;
    return (profiles ?? []) as UserProfile[];
  })();
  followCache.set(key, { at: Date.now(), promise });
  // A failure must not be cached for the whole window.
  promise.catch(() => followCache.delete(key));
  return promise;
}

export const useFollowing = () => {
  const { user, loading: authLoading } = useAuth();
  const [following, setFollowing] = useState<UserProfile[]>([]);
  const [followers, setFollowers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);

  const userId = user?.id ?? null;

  const fetchFollowing = useCallback(async () => {
    if (!userId) {
      setFollowing([]);
      return;
    }
    setLoading(true);
    try {
      setFollowing(await loadFollowList(userId, 'following'));
    } catch (error) {
      console.error('Error fetching following:', error);
      setFollowing([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchFollowers = useCallback(async () => {
    if (!userId) {
      setFollowers([]);
      return;
    }
    try {
      setFollowers(await loadFollowList(userId, 'followers'));
    } catch (error) {
      console.error('Error fetching followers:', error);
      setFollowers([]);
    }
  }, [userId]);

  const followUser = useCallback(async (targetUserId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_follows')
        .insert({ follower_id: user.id, following_id: targetUserId });

      if (error) throw error;
      invalidateFollowCache();

      // Create notification for the followed user
      await supabase
        .from('notifications')
        .insert({
          user_id: targetUserId,
          type: 'new_follower',
          from_user_id: user.id
        });
      
      await fetchFollowing();
      return true;
    } catch (error) {
      console.error('Error following user:', error);
      return false;
    }
  }, [user, fetchFollowing]);

  const unfollowUser = useCallback(async (targetUserId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', targetUserId);

      if (error) throw error;
      invalidateFollowCache();
      await fetchFollowing();
      return true;
    } catch (error) {
      console.error('Error unfollowing user:', error);
      return false;
    }
  }, [user, fetchFollowing]);

  const isFollowing = useCallback((targetUserId: string) => {
    return following.some(f => f.id === targetUserId);
  }, [following]);

  const searchUsers = useCallback(async (query: string): Promise<UserProfile[]> => {
    if (!user || !query.trim()) return [];

    try {
      const { data, error } = await supabase
        .rpc('search_public_profiles', { _query: query, _exclude: user.id, _limit: 10 });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }, [user]);

  const getFollowedTransmissions = useCallback(async (): Promise<FollowedTransmission[]> => {
    if (!user || following.length === 0) return [];

    try {
      const followingIds = following.map(f => f.id);
      const { data, error } = await supabase
        .from('transmissions')
        .select('id, title, author, cover_url, created_at, user_id')
        .in('user_id', followingIds)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Add user display names
      return (data || []).map(t => ({
        ...t,
        user_display_name: following.find(f => f.id === t.user_id)?.display_name || 'Unknown'
      }));
    } catch (error) {
      console.error('Error fetching followed transmissions:', error);
      return [];
    }
  }, [user, following]);

  useEffect(() => {
    // Only fetch if auth is ready and we have a user
    if (authLoading) return;
    
    if (!userId) {
      setFollowing([]);
      setFollowers([]);
      return;
    }

    fetchFollowing();
    fetchFollowers();
  }, [fetchFollowing, fetchFollowers, userId, authLoading]);

  return {
    following,
    followers,
    loading,
    followUser,
    unfollowUser,
    isFollowing,
    searchUsers,
    getFollowedTransmissions,
    refreshFollowing: fetchFollowing,
  };
};
